import { useEffect, useState } from 'react';
import PixelBackground from '@/components/PixelBackground';
import { Header } from '@/components/Header';
import { Footer } from '@/components/Footer';
import Seo from '@/components/Seo';
import Display from '@/components/typography/Display';
import Button from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { Container, HeroSection, Section } from '@/components/ui/Section';
import { useLanguage } from '@/contexts/LanguageContext';
import { fetchArchiveItems, ArchiveItem } from '@/lib/wordpress';
import { BASE_URL, getHreflangLinks, getPageMeta } from '@/seo/meta';
import { useLocation } from 'react-router-dom';
import { stripLanguagePrefix, withLanguagePrefix } from '@/lib/locale';

type Locale = 'en' | 'el';

interface ArchiveLayoutProps {
  pageKey: string;
  postType: string;
  query: string;
  title: Record<Locale, string>;
  description: Record<Locale, string>;
  eyebrow: Record<Locale, string>;
  emptyState: Record<Locale, string>;
  ctaText: Record<Locale, string>;
  ctaHref?: string;
}

export default function ArchiveLayout({
  pageKey,
  postType,
  query,
  title,
  description,
  eyebrow,
  emptyState,
  ctaText,
}: ArchiveLayoutProps) {
  const { language } = useLanguage();
  const locale: Locale = language === 'en' ? 'en' : 'el';
  const location = useLocation();
  const [items, setItems] = useState<ArchiveItem[]>([]);
  const [loading, setLoading] = useState(true);

  const meta = getPageMeta(pageKey, undefined, locale);
  const canonicalPath = withLanguagePrefix(stripLanguagePrefix(location.pathname), locale);
  const canonicalUrl = `${BASE_URL}${canonicalPath}`;
  const hreflang = getHreflangLinks(location.pathname, locale);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  useEffect(() => {
    const loadItems = async () => {
      setLoading(true);
      try {
        const result = await fetchArchiveItems<Record<string, { nodes: ArchiveItem[] }>>(query);
        // Map postType to the actual GraphQL response key
        const keyMap: Record<string, string> = {
          knowledge: 'knowledgeItems',
          legislation: 'legislationItems',
          faqs: 'faqs',
          guides: 'guides',
          tools: 'tools',
          organizations: 'organizations',
          resources: 'resources',
          posts: 'posts',
        };
        const graphqlKey = keyMap[postType.toLowerCase()] || postType.toLowerCase();
        const nodes = result[graphqlKey]?.nodes || [];
        setItems(nodes);
      } catch (error) {
        console.error(`Error loading ${postType}:`, error);
        setItems([]);
      } finally {
        setLoading(false);
      }
    };

    loadItems();
  }, [query, postType]);

  return (
    <div className="relative min-h-screen bg-black text-white">
      <Seo
        title={meta.title}
        description={meta.description}
        url={canonicalUrl}
        hreflang={hreflang}
        image={meta.image}
      />
      <PixelBackground />

      <div className="relative z-10 flex min-h-screen flex-col">
        <Header variant="simple" />

        <main id="main-content" className="flex-1">
          <HeroSection className="relative overflow-hidden pt-32 pb-20 md:pt-36 lg:pt-40 lg:pb-24">
            <div className="absolute inset-0 geometric-pattern" />
            <div className="absolute inset-0 bg-gradient-to-br from-pp-black/95 via-black/85 to-black" />

            <Container className="relative">
              <div className="max-w-3xl space-y-5">
                <p className="text-[11px] font-medium uppercase tracking-[0.4em] text-brand-highlight/70">
                  {eyebrow[locale]}
                </p>
                <Display
                  variant="h1"
                  as="h1"
                  className="text-white text-3xl sm:text-4xl md:text-5xl lg:text-6xl leading-[1.12] tracking-tight"
                >
                  {title[locale]}
                </Display>
                <Display variant="subhead" className="max-w-2xl">
                  {description[locale]}
                </Display>
              </div>
            </Container>
          </HeroSection>

          <Section className="py-12 lg:py-16">
            <Container>
              {loading ? (
                <div className="flex items-center justify-center py-20">
                  <div className="h-8 w-8 animate-spin rounded-full border-2 border-white/20 border-t-brand-highlight" />
                </div>
              ) : items.length === 0 ? (
                <div className="rounded-3xl border border-white/15 bg-black/70 p-12 text-center shadow-glass">
                  <p className="text-lg text-white/70">{emptyState[locale]}</p>
                </div>
              ) : (
                <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
                  {items.map((item) => (
                    <Card
                      key={item.id}
                      className="group rounded-3xl border-white/15 bg-black/70 p-0 shadow-glass transition-all duration-300 hover:-translate-y-1 overflow-hidden"
                    >
                      {item.featuredImage?.node && (
                        <div className="aspect-video overflow-hidden">
                          <img
                            src={item.featuredImage.node.sourceUrl}
                            alt={item.featuredImage.node.altText || item.title}
                            className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-105"
                          />
                        </div>
                      )}
                      <div className="p-6">
                        <h2 className="text-lg font-semibold text-white line-clamp-2">
                          {item.title}
                        </h2>
                        {(item.excerpt || item.content) && (
                          <div
                            className="mt-3 text-sm text-white/70 line-clamp-3"
                            dangerouslySetInnerHTML={{ __html: item.excerpt || item.content || '' }}
                          />
                        )}
                        <div className="mt-4 flex items-center justify-between">
                          <span className="text-xs text-white/50">
                            {new Date(item.date).toLocaleDateString(
                              locale === 'el' ? 'el-GR' : 'en-US',
                              {
                                year: 'numeric',
                                month: 'short',
                                day: 'numeric',
                              }
                            )}
                          </span>
                          <Button
                            as="a"
                            href={`/${postType.toLowerCase()}/${item.slug}`}
                            variant="ghost"
                            size="sm"
                            className="text-brand-highlight hover:text-brand-highlight/80"
                          >
                            {locale === 'el' ? 'Περισσότερα →' : 'Read more →'}
                          </Button>
                        </div>
                      </div>
                    </Card>
                  ))}
                </div>
              )}
            </Container>
          </Section>

          <Section className="pt-0">
            <Container>
              <div className="relative overflow-hidden rounded-[32px] border border-white/15 bg-black/70 p-10 text-center shadow-glass">
                <div className="pointer-events-none absolute -right-24 -top-32 h-72 w-72 rounded-full bg-brand-highlight/30 blur-3xl opacity-60" />
                <div className="pointer-events-none absolute -left-24 bottom-0 h-64 w-64 rounded-full bg-white/10 blur-3xl opacity-60" />
                <div className="relative">
                  <p className="text-[11px] font-medium uppercase tracking-[0.35em] text-brand-highlight/70">
                    {locale === 'el' ? 'ΧΡΕΙΑΖΕΣΤΕ ΒΟΗΘΕΙΑ;' : 'NEED HELP?'}
                  </p>
                  <Display
                    variant="h1"
                    as="h2"
                    className="mx-auto mt-4 max-w-3xl text-balance text-xl font-semibold text-white md:text-3xl"
                  >
                    {locale === 'el'
                      ? 'Δεν βρήκατε αυτό που ψάχνετε;'
                      : "Can't find what you're looking for?"}
                  </Display>
                  <p className="mx-auto mt-4 max-w-2xl text-center text-lg text-white/70">
                    {locale === 'el'
                      ? 'Επικοινωνήστε μαζί μας για περισσότερες πληροφορίες.'
                      : 'Contact us for more information.'}
                  </p>
                  <div className="mt-10 flex flex-col gap-4 text-center sm:flex-row sm:justify-center">
                    <Button
                      as="button"
                      onClick={() => window.navigateToConsultation?.()}
                      variant="primary"
                      size="lg"
                      className="w-full justify-center sm:w-auto"
                    >
                      {ctaText[locale]}
                    </Button>
                  </div>
                </div>
              </div>
            </Container>
          </Section>
        </main>

        <Footer variant="compact" />
      </div>
    </div>
  );
}
